// Entry point for the standalone registration / continue-registration pages
// (resources/views/site/event/shared/pages/registration/{index,continue}.blade.php).
//
// These are separate full-page-navigation documents, not a modal mounted on
// top of the landing page — so this is a second static webpack/Mix entry
// rather than a dynamic import() split. A dynamic import() only pays off when
// one running app instance decides at runtime whether to fetch a chunk (e.g.
// a client-side router, or a modal opened without leaving the page); here the
// server has already decided which page to render before any JS runs, so a
// second entry gives the same result with none of the added publicPath/
// chunk-manifest plumbing a dynamic import() would need.
//
// vee-validate, the Vuex store, and Stripe (pulled in transitively through
// registration/index.vue -> components/lp-payment.vue) only belong here —
// keeping them out of index.js (the plain landing page bundle) is the reason
// this file exists. A handful of small custom components (lp-button,
// lp-modal, …) are intentionally duplicated between the two entries rather
// than extracted into a shared vendor chunk: they're a few KB of our own SFCs,
// not npm libraries, and Mix's `extract()` only targets node_modules content
// — using it here would mean sharing it across every other app in
// webpack.mix.js, not just these two pages.
import { Plugin } from 'vue-fragment'
import installFragmentSiblingFix from '../global-declare-components/plugins/fragment-sibling-fix'
import Teleport from 'vue2-teleport';
import './plugins/vee-validate'
import { parseThemeParams, applyThemeToElement } from '../../helpers/landing-theme'
import createRegistrationStore from './store'

Vue.use(Plugin)
// Undo vue-fragment's over-broad sibling freeze (breaks vuedraggable / Vue patch).
installFragmentSiblingFix()

// Exposed for the registration/continue blades' inline `new Vue({ store })`.
window.createRegistrationStore = createRegistrationStore

// Theme preview: apply theme/brand tokens passed via URL query params to
// <body> on load — drives the "Customize theme" iframe preview, same as the
// landing page.
function initThemeFromQuery() {
  const state = parseThemeParams(window.location.search);
  if (Object.keys(state).length) {
    applyThemeToElement(document.body, state);
  }
}

if (document.readyState !== 'loading') {
  initThemeFromQuery();
} else {
  document.addEventListener('DOMContentLoaded', initThemeFromQuery);
}

Vue.directive('click-outside', {
  bind: function (el, binding, vnode) {
    el.clickOutsideEvent = function (event) {
      if (!(el == event.target || el.contains(event.target))) {
        vnode.context[binding.expression](event);
      }
    };
    document.body.addEventListener('click', el.clickOutsideEvent, true)
  },
  unbind: function (el) {
    document.body.removeEventListener('click', el.clickOutsideEvent, true)
  },
});

Vue.component('lp-teleport', Teleport)
Vue.component('lp-button', require('./ui/lp-button').default)
Vue.component('lp-modal', require('./ui/modals/lp-modal').default)
Vue.component('lp-drawer', require('./ui/modals/lp-drawer').default)

// Form field primitives.
Vue.component('lp-input', require('./ui/forms/lp-input').default)
Vue.component('lp-select', require('./ui/forms/lp-select').default)
Vue.component('lp-checkbox', require('./ui/forms/lp-checkbox').default)
Vue.component('lp-radio', require('./ui/forms/lp-radio').default)
Vue.component('lp-qty-stepper', require('./ui/forms/lp-qty-stepper').default)
Vue.component('lp-attend-toggle-inline', require('./ui/lp-attend-toggle-inline').default)

// Presenter/faculty surfaces shared with the landing page's drawers.
Vue.component('lp-avatar', require('./ui/lp-avatar').default)
Vue.component('lp-faculty-card', require('./ui/lp-faculty-card').default)
Vue.component('lp-presenter-list', require('./ui/lp-presenter-list').default)
Vue.component('lp-faculty-detail', require('./ui/lp-faculty-detail').default)
Vue.component('lp-session-detail', require('./ui/lp-session-detail').default)

// Schema-driven form field (registration attendee cards).
Vue.component('lp-form-field', require('./pages/registration/components/lp-form-field').default)

// Registration page (multi-step) — mounted via <registration-page> in the blade.
Vue.component('registration-page', require('./pages/registration/index').default)

// Continue-registration page — mounted via <continue-registration-page> in the
// continue.blade.php take-over (group-ticket attendees finishing their own
// registration + optional agenda sessions).
Vue.component('continue-registration-page', require('./pages/continue-registration/index').default)
